import React, { createContext, useState, useEffect, useContext } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext();
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);       // Supabase auth user object
  const [customer, setCustomer] = useState(null); // customers table row
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load customer profile from customers table
  const loadCustomer = async (userId) => {
    if (!userId) { setCustomer(null); return; }
    const { data } = await supabase
      .from('customers')
      .select('*')
      .eq('auth_user_id', userId)
      .maybeSingle();
    setCustomer(data ?? null);
  };

  useEffect(() => {
    // INITIAL_SESSION fires immediately with stored session
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      const u = session?.user ?? null;
      setUser(u);
      await loadCustomer(u?.id ?? null);
      setLoading(false);
    });
    return () => subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Auth methods ─────────────────────────────────────────────
  const login = async (email, password) => {
    setError(null);
    const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
    if (signInError) { setError(signInError.message); throw signInError; }
  };

  const loginWithGoogle = async () => {
    setError(null);
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/account` },
    });
    if (oauthError) { setError(oauthError.message); throw oauthError; }
  };

  const register = async (email, password, firstName, lastName) => {
    setError(null);
    const { error: signUpError } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { given_name: firstName, family_name: lastName } },
    });
    if (signUpError) { setError(signUpError.message); throw signUpError; }
    // The DB trigger auto-creates the customers row — no manual insert needed
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setCustomer(null);
  };

  const sendPasswordReset = async (email) => {
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (resetError) throw resetError;
  };

  const updatePassword = async (newPassword) => {
    const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
    if (updateError) throw updateError;
  };

  const updateProfile = async (updates) => {
    if (!customer?.id) throw new Error('Not logged in');
    const { data, error: updateError } = await supabase
      .from('customers')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', customer.id)
      .select()
      .single();
    if (updateError) throw updateError;
    setCustomer(data);
    return data;
  };

  return (
    <AuthContext.Provider value={{
      user,
      customer,
      loading,
      error,
      isAuthenticated: !!user,
      login,
      loginWithGoogle,
      register,
      logout,
      sendPasswordReset,
      updatePassword,
      updateProfile,
      refreshCustomer: () => loadCustomer(user?.id),
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
